import type { Device } from '@studio/models/device';
import type { PTZ } from '@studio/models/ptz';
import { hasMovement, Movements, Zero } from '@studio/models/ptz';
import { useDocumentActive, useLatestFn, useLazyRef, useUnmount } from '@studio/react-utils/hooks';
import { isJsonObject, isNil, isNotNil } from '@tryiris-ai/js-types';
import type { T_Optional } from '@tryiris-ai/js-types';
import { defaults } from 'lodash-es';
import { useEffect } from 'react';
import { Choreographer } from '../choreographer.js';
import { Joystick, QuickShots } from './constants.js';
import type { Wiring } from './types.js';
import { useAutoTrackingControlProtection } from '#~/auto-tracking/control-protection/hooks.js';
import type { Directive } from '#~/command/index.js';
import { Command } from '#~/command/index.js';
import { move, quickShots } from '#~/control/movement/commands/index.js';
import { usePiston, useTempoPref, useRatePref } from '#~/control/movement/hooks.js';
import { clamp, Facet, QuickShotSpeed, Range, Tempo } from '#~/control/movement/index.js';
import { raiseError } from '#~/notifications/index.js';
import { locateDevice } from '#~/room/index.js';

const useChoreographer = (deviceId: Device['id']): Choreographer => {
  const piston = usePiston(
    deviceId,
    useLatestFn((intent: PTZ) => {
      move(deviceId, intent, { toolUsed: 'gamepad' });
    })
  );

  const { isProtected, triggerAlert } = useAutoTrackingControlProtection(deviceId) ?? {};

  const handleMove = useLatestFn((intent: PTZ) => {
    if (isProtected && hasMovement(intent)) {
      triggerAlert?.();

      return;
    }

    piston.process(intent);
  });

  return useLazyRef(() => new Choreographer(handleMove)).current;
};

/** @internal */
export const useWiring = (deviceId: Device['id']): Wiring => {
  const choreographer = useChoreographer(deviceId);
  const isDocumentActive = useDocumentActive();
  const [ptTempo] = useTempoPref(deviceId, Facet.PanTilt);
  const [ptRate] = useRatePref(deviceId, Facet.PanTilt);
  const [zTempo] = useTempoPref(deviceId, Facet.Zoom);
  const [zRate] = useRatePref(deviceId, Facet.Zoom);

  const { isProtected, triggerAlert } = useAutoTrackingControlProtection(deviceId) ?? {};

  useUnmount(() => {
    // Stop all movement on unmount.
    choreographer.stop();
  });

  // Stop all movement when the document loses focus.
  useEffect(() => {
    if (!isDocumentActive) {
      choreographer.stop();
    }
  }, [choreographer, isDocumentActive]);

  // Handle gamepad joystick movement.
  const handleJoystick = useLatestFn((directive: Directive) => {
    const { command, data } = directive;

    // Stop?
    if (command === Command.PTZ_STOP) {
      choreographer.stop();

      return;
    }

    if (!isJsonObject(data)) {
      return;
    }

    // Parse directive data.
    const gamepadId = typeof data.gamepadId === 'string' ? data.gamepadId : undefined;
    const value = typeof data.value === 'number' ? data.value : undefined;

    if (!gamepadId || !value) {
      return;
    }

    const absValue = Math.abs(value);

    // Does this command involve the choreographer?
    const recipe = Joystick[command];

    if (!recipe || !isJsonObject(data)) {
      return;
    }

    const movement = recipe[Math.sign(value) >= 0 ? 'positive' : 'negative'];

    // Use the rate and tempo for the specified axis.
    let rateSpeed;
    if (command === Command.PTZ_ZOOM) {
      rateSpeed = (zTempo === Tempo.Fixed ? Range.max : absValue) * zRate.value;
    } else {
      rateSpeed = (ptTempo === Tempo.Fixed ? Range.max : absValue) * ptRate.value;
    }

    const speed = clamp(rateSpeed) * Math.sign(value); //
    choreographer.add(movement, speed);
  });

  // Handle quick shots.
  const handleQuickShots = useLatestFn(({ command }: Directive) => {
    const movement = QuickShots[command];

    if (isNil(movement)) {
      return;
    }

    const device = locateDevice(deviceId) ?? undefined;

    if (!device) {
      return;
    }

    const intent =
      movement === false
        ? Zero
        : ((): T_Optional<PTZ> => {
            const { axis, ptz } = Movements[movement];

            const currentValue = device.control?.movement?.quickShots?.[axis] ?? 0;
            const currentSign = Math.sign(currentValue);

            const nextValue = ptz[axis];
            const nextSign = Math.sign(nextValue);

            // Are we already moving in this direction?
            if (currentValue && currentSign === nextSign) {
              // We are just going stop motion along the specified axis.
              return {
                [axis]: 0
              };
            }

            return {
              [axis]: QuickShotSpeed * nextSign
            };
          })();

    if (isProtected && hasMovement(defaults({}, intent, Zero))) {
      triggerAlert?.();

      return;
    }

    quickShots(deviceId, intent).catch((error) => raiseError(error, { actionable: true }));
  });

  // Listen to incoming commands.
  return {
    listen: useLatestFn((directive: Directive) => {
      // Does this command involve the joystick?
      if (isNotNil(Joystick[directive.command])) {
        handleJoystick(directive);
      }

      // How about quick shots?
      if (isNotNil(QuickShots[directive.command])) {
        handleQuickShots(directive);
      }
    })
  };
};
